import React, { useState, useEffect } from 'react';

// BUG FIX: this modal previously had no state, no onClick on "Fetch
// History", and no `load` prop to know which assignment to fetch — it was
// pure decoration. Now wired to the real GET .../gps/history/ endpoint
// (loads/views.py::GPSHistoryView), which returns every saved GPS ping for
// that load (no server-side date filtering exists, so the date picker
// filters client-side against the returned points).
export function GPSModal({ load, onClose, apiUrl }) {
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // {tracking_started_at, tracking_stopped_at, points}

  useEffect(() => { setResult(null); setError(null); }, [load?.rawId]);

  const fetchHistory = async () => {
    if (!load?.rawId) { setError("No vehicle selected."); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const res = await fetch(`${apiUrl}/api/loads/assignments/${load.rawId}/gps/history/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError("Couldn't load GPS history. " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredPoints = (result?.points || []).filter(p => {
    if (!date) return true;
    return p.recorded_at?.slice(0, 10) === date;
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-fade-in-up max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-900">GPS Recall History</h3>
            {load && <p className="text-xs text-gray-400">{load.id} · {load.driver}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>
        <div className="p-6 overflow-y-auto custom-scrollbar">
          <p className="text-sm text-gray-500 mb-4">
            Retrieve past route data and breadcrumb trails for auditing purposes.
          </p>
          <div className="space-y-3">
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <button
              onClick={fetchHistory}
              disabled={loading || !load}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors"
            >
              {loading ? "Fetching…" : "Fetch History"}
            </button>
          </div>

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          {result && (
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pb-3 border-b border-gray-100">
                <span>Tracking started: <b className="text-gray-700">{result.tracking_started_at ? new Date(result.tracking_started_at).toLocaleString() : "—"}</b></span>
              </div>
              {filteredPoints.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  {date ? "No GPS pings recorded on that date." : "No GPS history recorded for this load yet."}
                </p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto custom-scrollbar">
                  {filteredPoints.map((p, i) => (
                    <div key={p.id || i} className="flex items-center gap-3 text-xs py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-gray-400 font-mono w-16 flex-shrink-0">
                        {new Date(p.recorded_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="text-gray-600 flex-1">{Number(p.lat).toFixed(4)}, {Number(p.lng).toFixed(4)}</span>
                      <span className="text-gray-400 flex-shrink-0">{p.speed != null ? `${Math.round(p.speed)} mph` : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-3 text-right">{filteredPoints.length} of {result.points?.length || 0} points shown</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function MapPopupModal({ load, onClose, onOpenChat }) {
  if (!load) return null;
  const initials = (load.driver || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
  const statusColor = {
    "In Progress": "bg-blue-100 text-blue-700",
    "Pending": "bg-orange-100 text-orange-600",
    "Completed": "bg-green-100 text-green-700",
    "Delayed": "bg-red-100 text-red-600",
  }[load.status] || "bg-gray-100 text-gray-600";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-fade-in-up" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 truncate">{load.driver || "Unassigned"}</p>
            <p className="text-xs text-gray-400">{load.id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold flex-shrink-0">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${statusColor}`}>{load.status}</span>
            <span className="flex items-center gap-1.5 text-xs">
              <span className={`w-1.5 h-1.5 rounded-full ${load.isTrackingActive ? "bg-green-500" : "bg-gray-300"}`}/>
              {load.isTrackingActive ? "GPS active" : "GPS off"}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate">{load.origin} → {load.destination}</p>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase">Speed</p>
              <p className="text-sm font-bold text-gray-900">{load.speed != null ? `${load.speed} mph` : "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase">ETA</p>
              <p className="text-sm font-bold text-gray-900">{load.eta || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase">Remaining</p>
              <p className="text-sm font-bold text-gray-900">{load.remaining || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase">Updated</p>
              <p className="text-sm font-bold text-gray-900">{load.lastUpdate || "—"}</p>
            </div>
          </div>
          {load.lat != null && load.lng != null && (
            <p className="text-[11px] text-gray-400 font-mono pt-1">{load.lat.toFixed(4)}, {load.lng.toFixed(4)}</p>
          )}
          <button
            onClick={onOpenChat}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm mt-2"
          >
            💬 Message {load.driver?.split(" ")[0] || "driver"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar destination panels ──────────────────────────────────────────────
// These back the sidebar items that used to just show a "coming soon" toast
// (Drivers, Vehicles, Reports, Settings, Documents). Drivers/Vehicles/Reports/
// Settings are wired to real endpoints where the backend actually supports
// them; Documents has no backing model anywhere in the schema yet, so it
// stays an honest "coming soon" panel rather than faking data.

function PanelShell({ title, subtitle, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} overflow-hidden shadow-2xl animate-fade-in-up max-h-[85vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold flex-shrink-0">×</button>
        </div>
        <div className="overflow-y-auto custom-scrollbar flex-1">{children}</div>
      </div>
    </div>
  );
}

// Shared "not built yet" banner — same enterprise-tier language used on the
// pricing page's Optional Add-Ons, so the tone is consistent everywhere the
// product says "not yet" instead of faking it.
function ComingSoonBanner({ feature }) {
  return (
    <div className="m-6 p-5 rounded-xl border border-dashed border-blue-200 bg-blue-50/50 text-center">
      <span className="inline-block text-[10px] font-black tracking-wider uppercase bg-blue-600 text-white px-2.5 py-1 rounded-full mb-3">
        Enterprise · Coming Soon
      </span>
      <p className="text-sm font-semibold text-gray-800">{feature} isn't built yet</p>
      <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
        Real-time visibility, faster coordination, complete control — on the roadmap for a future release.
      </p>
    </div>
  );
}

export function DriversPanel({ onClose, apiUrl, onSelectDriver }) {
  const [state, setState] = useState({ loading: true, error: null, drivers: [] });
  useEffect(() => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    fetch(`${apiUrl}/api/dispatcher/drivers/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(res.status === 403 ? "Dispatcher access only." : `Server returned ${res.status}`); return res.json(); })
      .then(data => setState({ loading: false, error: null, drivers: data.drivers || [] }))
      .catch(err => setState({ loading: false, error: err.message, drivers: [] }));
  }, [apiUrl]);

  return (
    <PanelShell title="Drivers" subtitle={`${state.drivers.length} on your roster`} onClose={onClose} wide>
      <div className="p-4">
        {state.loading && <p className="text-sm text-gray-400 text-center py-8">Loading drivers…</p>}
        {state.error && <p className="text-sm text-red-600 text-center py-8">{state.error}</p>}
        {!state.loading && !state.error && state.drivers.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No drivers on your roster yet.</p>
        )}
        <div className="space-y-2">
          {state.drivers.map(d => (
            <div key={d.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">{d.avatar}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{d.name}</p>
                <p className="text-xs text-gray-400 truncate">{d.vehicle || "No vehicle on file"}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${d.status === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{d.status}</span>
                {d.speed > 0 && <p className="text-[10px] text-gray-400 mt-1">{Math.round(d.speed)} mph</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PanelShell>
  );
}

export function VehiclesPanel({ onClose, apiUrl }) {
  // No dedicated Vehicle model exists on the backend — vehicle info lives
  // as free-text (year/make/model) on each driver's profile. This derives
  // a vehicles view from that same roster data rather than pretending a
  // richer fleet-management model exists.
  const [state, setState] = useState({ loading: true, error: null, drivers: [] });
  useEffect(() => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    fetch(`${apiUrl}/api/dispatcher/drivers/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(res.status === 403 ? "Dispatcher access only." : `Server returned ${res.status}`); return res.json(); })
      .then(data => setState({ loading: false, error: null, drivers: data.drivers || [] }))
      .catch(err => setState({ loading: false, error: err.message, drivers: [] }));
  }, [apiUrl]);

  const withVehicles = state.drivers.filter(d => d.vehicle);

  return (
    <PanelShell title="Vehicles" subtitle={`${withVehicles.length} on file`} onClose={onClose} wide>
      <div className="p-4">
        <p className="text-xs text-gray-400 mb-3 px-1">
          Derived from each driver's profile — there's no separate fleet/vehicle registry yet, so this is one row per driver-reported vehicle.
        </p>
        {state.loading && <p className="text-sm text-gray-400 text-center py-8">Loading vehicles…</p>}
        {state.error && <p className="text-sm text-red-600 text-center py-8">{state.error}</p>}
        {!state.loading && !state.error && withVehicles.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No vehicle info on file for any driver yet.</p>
        )}
        <div className="space-y-2">
          {withVehicles.map(d => (
            <div key={d.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-lg flex-shrink-0">🚛</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{d.vehicle}</p>
                <p className="text-xs text-gray-400 truncate">Driver: {d.name}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${d.status === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{d.status}</span>
            </div>
          ))}
        </div>
      </div>
    </PanelShell>
  );
}

export function ReportsPanel({ onClose, apiUrl }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    fetch(`${apiUrl}/api/loads/analytics/company/?timeframe=30`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(`Server returned ${res.status}`); return res.json(); })
      .then(data => setState({ loading: false, error: null, data }))
      .catch(err => setState({ loading: false, error: err.message, data: null }));
  }, [apiUrl]);

  return (
    <PanelShell title="Reports" subtitle="Last 30 days" onClose={onClose} wide>
      {state.loading && <p className="text-sm text-gray-400 text-center py-8">Loading report…</p>}
      {state.error && <p className="text-sm text-red-600 text-center py-8 px-6">{state.error}</p>}
      {state.data && (
        <div className="p-5">
          {/* NOTE: this endpoint filters by Load.company == the logged-in
              account, which is scoped to a "company" role account, not
              necessarily a "dispatcher" role one — if you're logged in as
              a pure dispatcher rather than the company account, this may
              correctly show zero loads rather than an error. */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${state.data.total_spent?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wide">Total Loads</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{state.data.total_loads || 0}</p>
            </div>
          </div>
          <p className="text-xs font-semibold text-gray-500 mb-2">Daily breakdown</p>
          <div className="space-y-1 max-h-56 overflow-y-auto custom-scrollbar">
            {(state.data.chart_data || []).filter(d => d.loads > 0).length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">No load activity in this window.</p>
            )}
            {(state.data.chart_data || []).map(d => (
              <div key={d.date} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                <span className="text-gray-500">{d.date}</span>
                <span className="text-gray-700">{d.loads} loads</span>
                <span className="font-semibold text-gray-900">${d.spent.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </PanelShell>
  );
}

export function SettingsPanel({ onClose, apiUrl, user, onSaved }) {
  const [form, setForm] = useState({ full_name: user?.full_name || "", company_name: user?.company_name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const res = await fetch(`${apiUrl}/api/me/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const updated = await res.json();
      setSaved(true);
      onSaved?.(updated);
    } catch (err) {
      setError("Couldn't save changes. " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PanelShell title="Settings" subtitle="Your dispatcher profile" onClose={onClose}>
      <div className="p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-gray-500">Full name</label>
          <input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500">Company name</label>
          <input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500">Phone</label>
          <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        {saved && <p className="text-xs text-green-600">Saved.</p>}
        <button onClick={save} disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </PanelShell>
  );
}

export function DocumentsPanel({ onClose }) {
  return (
    <PanelShell title="Documents" onClose={onClose}>
      <ComingSoonBanner feature="Document storage" />
    </PanelShell>
  );
}